import {
  effectScope,
  onMounted,
  onScopeDispose,
  watch,
  type WatchSource,
} from 'vue';
import { useScrollLock } from '@vueuse/core';

/**
 * Locks scrolling of `document.body` while the given source is truthy.
 *
 * Drop-in replacement for the Vue 2-only `v-scroll-lock` directive, built on
 * `@vueuse/core`'s `useScrollLock`, so it works with both Vue 2.7 and Vue 3.
 *
 * All callers share a single `useScrollLock(document.body)` instance created in
 * a detached effect scope. This avoids the multi-instance clobbering that
 * happens when several `useScrollLock` instances target the same element (each
 * would re-capture the — already `hidden` — overflow as the "initial" value and
 * never restore it). A reference count keeps the body locked until the last
 * open overlay (modal, drawer, mobile menu…) releases it.
 */

let sharedLock: ReturnType<typeof useScrollLock> | null = null;
let lockCount = 0;

function getSharedLock() {
  if (!sharedLock) {
    // Detached scope → the internal watcher lives for the app's lifetime and is
    // not disposed when the component that first requested a lock unmounts.
    effectScope(true).run(() => {
      sharedLock = useScrollLock(document.body);
    });
  }
  return sharedLock as ReturnType<typeof useScrollLock>;
}

export default function useBodyScrollLock(source: WatchSource<boolean>) {
  let lockedByThis = false;

  const setLocked = (value: unknown) => {
    const next = Boolean(value);
    if (next === lockedByThis) return;
    lockedByThis = next;

    const lock = getSharedLock();
    lockCount = Math.max(0, lockCount + (next ? 1 : -1));
    lock.value = lockCount > 0;
  };

  onMounted(() => {
    watch(source, setLocked, { immediate: true });
  });

  // Release the lock if the component is torn down while still locked.
  onScopeDispose(() => setLocked(false));
}
